import React from 'react';
import Button from '@material-ui/core/Button';
import {browserHistory, Link} from 'react-router';
import SearchStore from 'stores/SearchStore';
import LibraryTagCallsSearchDropdown from 'components/pure/calls/library/LibraryTagCallsSearchDropdown';
import CallActions from 'actions/CallActions';
import FileUploadNewCallActions from 'actions/file-upload/FileUploadNewCallActions';
import TrendPivotChartActions from 'actions/calls/trend/TrendPivotChartActions';
import ConstValues from 'utils/const';
import CallerTeamComboboxSearchFieldActions from 'actions/search/CallerTeamComboboxSearchFieldActions';
import ChevronLeftIcon from 'components/pure/utils/icon/ChevronLeftIcon';
import ComponentUtils from 'components/ComponentUtils';
import CallStore from 'stores/files/CallStore';
import DashMetricsActions from 'actions/dashboard/DashMetricsActions';
import {getQuery, getStringQueryParam} from 'utils/getQueryParam';
import CallSortControl from './calls-sort-control';
import FileUploadNewCallDialogContainer from './file-upload/FileUploadNewCallDialogContainer';
import SearchCountWidgetContainer from './search-count-widget';
import SearchInputBoxContainer from './search-input-box';
import TrendPivotChartContainer from './trend';
import './CallsComponent.styl';
import SearchFieldsStore from 'stores/search/SearchFieldsStore';
import {UploadIcon} from '@components/icon/UploadIcon';
import {RefreshIcon} from '@components/icon/RefreshIcon';
import SidebarActions from 'actions/SidebarActions';

const st = ConstValues.searchTypes;

class CallsComponent extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      openFileUploadDialog: false,
      backUrl: undefined,
      limitWarning: undefined,
    };
    this.utils = new ComponentUtils();
    this.advancedSearch = this.advancedSearch.bind(this);
    this.clearSearch = this.clearSearch.bind(this);
    this.closeNewCallDialog = this.closeNewCallDialog.bind(this);
    this.onClickTrendPivotButton = this.onClickTrendPivotButton.bind(this);
    this.onTouchTapFileUploadNewCallDialogCloseButton =
      this.onTouchTapFileUploadNewCallDialogCloseButton.bind(this);
    this.rerunCurrentSearch = this.rerunCurrentSearch.bind(this);
    this.searchTermChanged = this.searchTermChanged.bind(this);
    this.setupForRepSearch = this.setupForRepSearch.bind(this);
  }

  searchTermChanged(term) {
    if (term.length > 0) {
      // If `term` is valid, then we also want to hide the "Advanced Search" button link
      this.props.CallActions.setIsAdvancedSearch(true);
    }
    const searchType = SearchStore.getState().get('searchType');
    setImmediate(() => {
      const searchExpression = SearchFieldsStore.getState().get('searchExpression');
      if (term.length > 0 && searchExpression.size > 0) {
        // User searches for a valid term with some search fields / expression attached
        this.props.CallActions.termFieldsSearch(term, searchExpression);
      } else if (term.length === 0 && searchType !== st.miAll) {
        // User clears the search input box while searching for a non-all-type
        this.resetSearchFieldWithType(searchType);
      } else {
        // User searches for a valid term without any search fields attached
        this.props.CallActions.textSearch(term);
      }
    });
  }

  /** Clear search fields with `searchType` from `SearchStore` and `SearchFieldsStore` */
  resetSearchFieldWithType(searchType) {
    CallActions.updateSearchType([searchType, searchType]);
    CallActions.removeSearchType([searchType]);
  }

  rerunCurrentSearch() {
    this.utils.refreshCurrentSearch();
  }

  setupForRepSearch(repToSearchFor) {
    // Clear any existing search, turn advanced search on, limit to calls made by the plan recipient.
    CallActions.clearSearch();
    CallActions.setIsAdvancedSearch(true);
    CallerTeamComboboxSearchFieldActions.changeCaller({
      caller: repToSearchFor,
      selectedOptionKey: 'rep_search',
    });
  }

  /**
   * <CallsComponent /> is mounted for the first time. If this is mounted with a "All Calls" tab, then we use the
   * current active search to update the
   * calls inside <CallsComponent /> to the defaults ones belong to the current user *or* perform a vanilla
   * text-based search if the current active search is empty, i.e., when <CallsComponent /> is mounted
   * for the first time. If this is mounted with a "My Calls" tab, then we check the reviewRedirect flag.
   * If that flag is set then we perform a search for a specific rep's calls. Otherwise we use this.props.user
   * to populate the Caller search field and make a search request to update the calls inside <CallsComponent />
   * to ones called by this.props.user.
   */
  componentDidMount() {
    // Update fileId value in store, if it is in the querystring
    const {fileId, fromEmail} = getQuery(this.props.location);
    const routerState = this.props.location.state;
    // easier than individually defer'ing
    setTimeout(async () => {
      if (fileId) {
        // Query included a specific file ID. Redirect to that call.
        browserHistory.replace({
          pathname: `/call/${fileId}`,
          query: {
            fromEmail: fromEmail || false,
          },
        });
      }

      if (routerState) {
        const {dontReset} = routerState;
        // Clear link state after we make use of it
        // otherwise it can prevent loading on refresh
        if (dontReset) {
          return browserHistory.replace({
            pathname: window.location.pathname,
            state: {
              ...routerState,
              dontReset: false,
            },
          });
        }
      }

      if (this.props.isSearchingLibraryTags) {
        // User is searching libraries. Handled by `CallActions.libraryTagsSearch`
        this.props.CallActions.libraryTagsSearch();
      } else if (!!SearchStore.getState().get('isSearchingSelf')) {
        // User is searching their own calls. Handled by `CallActions.selfSearch`
        this.props.CallActions.setIsSearchingSelf(true);
        this.props.CallActions.selfSearch();
      } else if (this.props.repToSearchFor) {
        this.setupForRepSearch(this.props.repToSearchFor);
      } else {
        // Any other search.
        this.props.CallActions.setIsSearchingSelf(false);
        this.props.CallActions.myCallsSearch();
      }
    });
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.repToSearchFor && !this.props.repToSearchFor) {
      // Playlist was closed.
      this.clearSearch();
    }
  }

  /**
   * Close the new call dialog by updating this.state
   */
  closeNewCallDialog() {
    this.setState({openFileUploadDialog: false});
    FileUploadNewCallActions.updateCloseNewCallDialog(false);
  }

  /**
   * Close the "Add New Call" dialog
   */
  onTouchTapFileUploadNewCallDialogCloseButton() {
    this.setState({openFileUploadDialog: false});
    setImmediate(() => {
      FileUploadNewCallActions.clearState();
    });
  }

  clearSearch() {
    this.props.CallActions.updateIsInitialLoad(true);
    this.toggleOffTrendPivotChart();
    const {repToSearchFor} = this.props;
    setTimeout(() => {
      if (repToSearchFor) {
        this.setupForRepSearch(repToSearchFor);
      } else {
        this.props.CallActions.clearSearch();
        CallActions.clearDrilldown();
        DashMetricsActions.clearTopicIds();
      }
    }, 100);
  }

  /**
   * Return `true` if a <react-select /> component should be rendered as part of the whole
   * <CallsComponent />
   * @return {boolean} `true` if a <react-select /> component should be rendered as part of
   * the whole <CallsComponent />
   */
  shouldRenderLibraryTagDropdown() {
    return this.props.libraryTags && this.props.libraryTags.count() > 0;
  }

  shouldRenderClearSearchButton() {
    // only show button when there is text on the input, when there is an advanced search
    // or when searching for a specific file
    const fileId = getStringQueryParam(this.props.location, 'fileId');
    const fileIdInState = SearchStore.getState().get('fileId');
    return this.props.searchTerm || this.props.isAdvancedSearch || fileId || fileIdInState;
  }

  /**
   * Returns a boolean that indicates whether the Trend Chart should be shown.
   * @return {boolean} When true indicates that the Trend Chart should be shown.
   **/
  shouldShowTrendChart() {
    return this.props.showTrendChart;
  }

  /**
   * Toggles the variable tracking if the Trend Chart should be shown.
   **/
  onClickTrendPivotButton() {
    TrendPivotChartActions.toggleShowTrendChart();
  }

  /**
   * Toggles the variable tracking if the Trend Chart should be shown to off no matter the current visibility.
   **/
  toggleOffTrendPivotChart() {
    if (this.shouldShowTrendChart()) {
      TrendPivotChartActions.toggleShowTrendChart();
    }
  }

  renderClearSearchButton() {
    if (this.shouldRenderClearSearchButton()) {
      return (
        <div className="clear-search" onClick={this.clearSearch}>
          Clear Search
        </div>
      );
    }
  }

  renderSearchCountWidget() {
    if (this.shouldRenderClearSearchButton()) {
      return <SearchCountWidgetContainer />;
    }
  }

  renderTrendButton() {
    if (this.props.showTrendButton) {
      return (
        <div className="trend-pivot-button">
          <Button
            variant="outlined"
            className="search-button"
            onClick={this.onClickTrendPivotButton}>
            Trend
          </Button>
        </div>
      );
    }
  }

  /** Show the "search sidebar" without making a round-trip to the server-backend */
  advancedSearch() {
    CallActions.setIsAdvancedSearch(true);
    SidebarActions.open();
  }

  renderAdvancedSearchButton() {
    const fileId = getStringQueryParam(this.props.location, 'fileId');
    const fileIdInState = SearchStore.getState().get('fileId');

    // only show button when advanced search is off, and there is no fileId search.
    const isButtonVisible = !this.props.isAdvancedSearch && !fileId && !fileIdInState;
    if (isButtonVisible) {
      return (
        <div className="advanced-search bold" onClick={this.advancedSearch}>
          Advanced Search
        </div>
      );
    }
    return null;
  }

  /** Render the trend/pivot feature chart */
  renderTrendChart() {
    if (this.props.showTrendButton && this.shouldShowTrendChart()) {
      return <TrendPivotChartContainer />;
    }
  }

  maybeRenderClickthroughNav() {
    const {limitWarning, backUrl, backText} = CallStore.getState();

    return (
      <div className={'clickthrough-nav'}>
        {backText && backUrl && (
          <Link className="generic-link clickthrough-link" to={backUrl}>
            <ChevronLeftIcon className="icon chevron-left" />
            {backText}
          </Link>
        )}
        {limitWarning && <p>{limitWarning}</p>}
      </div>
    );
  }

  /**
   * Render a link to revert to rep filtering when the playlist is shown
   * in Add Calls Mode
   */
  renderRepCallsLink() {
    const {location, focusedCall} = this.props;
    return (
      <Link
        className="generic-link rep-filter-link"
        to={{
          pathname: '/calls/my',
          state: location.state,
        }}>
        <ChevronLeftIcon />
        Back to {focusedCall.caller.displayName}'s calls
      </Link>
    );
  }

  render() {
    const {showRepFilterLink} = this.props;

    const hasDrilldownApplied = !!CallStore.getState().drilldownCalls;

    return (
      <div className="calls-component">
        {!this.props.isScorecardToggled && (
          <div className="calls-top-nav">
            <div className="nav-row">
              <div className="menu-group advanced-search-controls">
                {!hasDrilldownApplied && (
                  <SearchInputBoxContainer searchTermChanged={this.searchTermChanged} />
                )}
                <div className="advanced-search-toggle">
                  {!hasDrilldownApplied && this.renderAdvancedSearchButton()}
                  {this.renderClearSearchButton()}
                  {this.renderSearchCountWidget()}
                </div>
                {this.renderTrendButton()}
              </div>
              {this.shouldRenderLibraryTagDropdown() ? (
                <LibraryTagCallsSearchDropdown {...this.props} />
              ) : (
                <Button
                  className="button-with-icon"
                  onClick={() => this.setState({openFileUploadDialog: true})}>
                  <UploadIcon />
                  Upload Previous Call
                </Button>
              )}
            </div>
            <div className="nav-row">
              {!hasDrilldownApplied && (
                <CallSortControl
                  sortByDate={this.props.sortByDate}
                  refreshCurrentSearch={this.rerunCurrentSearch}
                />
              )}
              <Button
                className="button-with-icon borderless bold"
                onClick={this.rerunCurrentSearch}>
                <RefreshIcon />
                Refresh List
              </Button>
            </div>
          </div>
        )}
        {this.renderTrendChart()}
        {this.maybeRenderClickthroughNav()}
        <div className="calls">
          {showRepFilterLink && this.renderRepCallsLink()}
          {this.props.children}
        </div>
        <FileUploadNewCallDialogContainer
          open={this.state.openFileUploadDialog}
          handleCloseNewCallDialog={this.closeNewCallDialog}
          onTouchTapCloseButton={this.onTouchTapFileUploadNewCallDialogCloseButton}
        />
      </div>
    );
  }
}

CallsComponent.displayName = 'CallsComponent';
export default CallsComponent;
